// chartHelpers.js

import { formatProjectNumber } from "../../utils/helpers";

export function generateLabels(chartStartDate, intervalMinutes = 15, startTime = "00:00") {
  // Parse startTime (HH:mm format) to get hours and minutes
  const [startHour, startMinute] = (startTime || "00:00").split(":").map(Number);
  const dayKey = chartStartDate 
    ? `${new Date(new Date(chartStartDate).setHours(0,0,0,0)).toISOString().slice(0,10)}_${startTime}` 
    : `AUTO_LASTYEAR_${startTime}`;
  const INTERVAL = intervalMinutes * 60 * 1000;
  const ONE_DAY = 24 * 60 * 60 * 1000;
  const startBase = chartStartDate ? new Date(chartStartDate) : new Date(Date.now() - 365*ONE_DAY);
  // Set the start time to the specified hour and minute
  const start = new Date(startBase);
  start.setHours(startHour, startMinute, 0, 0);
  const endTime = start.getTime() + 365*ONE_DAY;
  const count = Math.ceil((endTime - start.getTime())/INTERVAL);
  const tmp = new Array(count);
  let t = start.getTime();
  for (let i=0;i<count;i++, t+=INTERVAL) tmp[i] = new Date(t);
  return { labels: tmp, key: dayKey };
}

// Generates labels for a single 24-hour day anchored to baseDate (defaults to Jan 1 of previous year).
// Used when recurrence is DAILY — interval drives how many points fit in 24h.
export function generateDailyLabels(intervalMinutes = 15, baseDate) {
  const ONE_DAY = 24 * 60 * 60 * 1000;
  const base = baseDate ? new Date(baseDate) : new Date(new Date().getFullYear() - 1, 0, 1);
  const start = new Date(base);
  start.setHours(0, 0, 0, 0);
  const key = `DAILY_${intervalMinutes}_${start.toISOString().slice(0, 10)}`;
  const INTERVAL = intervalMinutes * 60 * 1000;
  const count = Math.ceil(ONE_DAY / INTERVAL);
  // count + 1: last entry is the end-of-day sentinel (00:00 next day)
  // so d3.extent(labels) spans the full 24 hours
  const tmp = new Array(count + 1);
  let t = start.getTime();
  for (let i = 0; i <= count; i++, t += INTERVAL) tmp[i] = new Date(t);
  return { labels: tmp, key };
}

export function formatValue(value) {
  if (value == null || isNaN(value)) return "0.00";
  const num = Number(value);
  return formatProjectNumber(num, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function calculateStats(data) {
  if (!data.length) return { max: '0', min: '0', mean: '0', energy: '0' };
  let max = -Infinity, min = Infinity, sum = 0;
  for (let i=0;i<data.length;i++) {
    const v = data[i];
    if (typeof v === 'number' && !isNaN(v)) {
      if (v > max) max = v;
      if (v < min) min = v;
      sum += v;
    }
  }
  if (max === -Infinity) { max = 0; min = 0; sum = 0; }
  const mean = sum / data.length;
  const energy = mean * data.length;
  return { max: formatValue(max), min: formatValue(min), mean: formatValue(mean), energy: formatValue(energy) };
}

export function progressiveSample(arr, labels, innerWidth) {
  if (labels.length <= innerWidth * 1.2) return arr;
  const stride = Math.max(1, Math.floor(labels.length / innerWidth));
  const idx = [];
  for (let i=0;i<labels.length;i+=stride) idx.push(i);
  if (idx[idx.length-1] !== labels.length-1) idx.push(labels.length-1);
  const out = new Array(idx.length);
  for (let i=0;i<idx.length;i++) out[i] = arr[idx[i]];
  return out;
}

// ─── Shared time-series constants ────────────────────────────────────────────
export const fontFamily = "Nunito, sans-serif";
export const stepMs = 15 * 60 * 1000;

// Electrical demand series config — shared between resultSeries and ResultSeriesSync
export const SERIES_CONFIG = [
  { id: "load",    labels: ["load", "loads"],                                                          color: "#4992ff", label: "Load"   },
  { id: "battery", labels: ["battery", "batterypower"],                                                color: "#7cffb2", label: "BESS"   },
  { id: "pv",      labels: ["pv", "solargeneration", "solar", "solar_generation", "solar-generation"], color: "#58d9f9", label: "PV"     },
  { id: "genset",  labels: ["diesel", "dieselgeneration", "genset", "generator"],                      color: "#05c091", label: "Genset" },
  { id: "grid",    labels: ["grid", "gridpower", "grid_total", "gridtotal", "grid-power"],             color: "#ff9f40", label: "Grid"   },
  { id: "evcs",    labels: ["evcs", "evcharging", "evchargers", "ev_charging", "ev_chargers"],        color: "#9a60b4", label: "EVCS"   },
];

export const SOC_CONFIG = {
  id: "battery_soc",
  labels: ["batterysoc", "battery_soc", "battery-soc", "stateofcharge", "state_of_charge", "soc"],
  color: "#f66d9b",
  label: "Battery SoC",
};

// ─── Binary-search helpers ────────────────────────────────────────────────────
/** First index i where arr[i] >= target */
export const leftIndex = (arr, target) => {
  let lo = 0, hi = arr.length;
  while (lo < hi) { const mid = (lo + hi) >> 1; arr[mid] < target ? (lo = mid + 1) : (hi = mid); }
  return lo;
};

/** First index i where arr[i] > target */
export const rightIndex = (arr, target) => {
  let lo = 0, hi = arr.length;
  while (lo < hi) { const mid = (lo + hi) >> 1; arr[mid] <= target ? (lo = mid + 1) : (hi = mid); }
  return lo;
};

/** Index of the element in arr[] nearest to target */
export const nearestIndex = (arr, target) => {
  if (!arr?.length || !Number.isFinite(target)) return -1;
  const i = leftIndex(arr, target);
  if (i <= 0) return 0;
  if (i >= arr.length) return arr.length - 1;
  return Math.abs(arr[i] - target) < Math.abs(arr[i - 1] - target) ? i : i - 1;
};

/**
 * Binary-search nearest rendered Highcharts point in a sorted points array.
 * With dataGrouping ON and boost disabled, s.points holds grouped rendered points.
 * pt.x = first raw timestamp of the group — a genuine timesMs[] value.
 */
export const nearestRenderedPoint = (points, x) => {
  if (!points?.length) return null;
  let lo = 0, hi = points.length;
  while (lo < hi) { const mid = (lo + hi) >> 1; points[mid].x <= x ? (lo = mid + 1) : (hi = mid); }
  if (lo <= 0) return points[0];
  if (lo >= points.length) return points[points.length - 1];
  return Math.abs(points[lo].x - x) < Math.abs(points[lo - 1].x - x) ? points[lo] : points[lo - 1];
};

/** Converts a raw SoC value (0–1 or 0–100) to a percentage (0–100) */
export const toSocPercent = (v) => {
  const n = +v;
  if (!Number.isFinite(n)) return null;
  return n >= 0 && n <= 1 ? n * 100 : n;
};
